<template>
  <div class="list-customers-page">
    <PageHeader
      :title="t('myShopData.customers.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="ListsCustomers">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'customers' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: fullname (firstname, lastname, created_at, updated_at) -->
          <template #cell-fullname="{ data }">
            <div class="fullname-cell">
              <div class="name-row">
                <span
                  v-if="formatFullName(data.first_name, data.last_name)"
                  class="contact-name"
                >
                  {{ formatFullName(data.first_name, data.last_name) }}
                </span>
                <span
                  v-else
                  class="contact-name contact-name--empty"
                >
                  {{ t('myLists.manage.customers.noName') }}
                </span>
                <SpmBadge
                  :label="`#${data.customer_id}`"
                  :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
                  size="sm"
                  variant="soft"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: email -->
          <template #cell-email="{ data }">
            <span v-if="cleanStr(data.email)">{{ cleanStr(data.email) }}</span>
            <span
              v-else
              class="cell-empty"
            >-</span>
          </template>

          <!-- Single column: gender -->
          <template #cell-gender="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="cleanStr(data.gender) === '1'"
                v-tooltip.top="t('gender.male')"
                class="fa-solid fa-mars gender-icon gender-icon--male"
              />
              <i
                v-else-if="cleanStr(data.gender) === '2'"
                v-tooltip.top="t('gender.female')"
                class="fa-solid fa-venus gender-icon gender-icon--female"
              />
              <i
                v-else
                v-tooltip.top="t('gender.unknown')"
                class="fa-solid fa-genderless gender-icon gender-icon--unknown"
              />
            </div>
          </template>

          <!-- Single column: birthday -->
          <template #cell-birthday="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span v-if="cleanStr(data.birth_date)">
                <SpmDateDisplay
                  :value="data.birth_date"
                  format="date"
                  icon="fa-light fa-cake-candles"
                  size="sm"
                />
              </span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: newsletter -->
          <template #cell-newsletter="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_newsletter_subscribed === '1' || data.is_newsletter_subscribed === 1"
                v-tooltip.top="t('myLists.manage.customers.optin.1')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('myLists.manage.customers.optin.0')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>

          <!-- Composite column: location (country, lang) -->
          <template #cell-location="{ data }">
            <div class="location-cell">
              <!-- Country flag or neutral icon -->
              <div
                v-if="cleanStr(data.country)"
                v-tooltip.top="t(`countries.codes.${cleanStr(data.country).toUpperCase()}`)"
              >
                <Flag :country="cleanStr(data.country)" />
              </div>
              <div
                v-else
                v-tooltip.top="t('myLists.manage.customers.noCountry')"
              >
                <i
                  class="fa fa-globe"
                  style="color: #d1d5db;"
                />
              </div>

              <!-- Language badge -->
              <span
                v-if="cleanStr(data.lang)"
                v-tooltip.top="t(`languages.codes.${cleanStr(data.lang)}`)"
                class="lang-badge cursor-pointer"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
            </div>
          </template>

          <!-- Single column: active -->
          <template #cell-active="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_active === '1' || data.is_active === 1"
                v-tooltip.top="t('active')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('inactive')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import Flag from '@/components/table/renderers/Flag.vue';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'CustomersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
    Flag,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    // Base filters that are always applied (not source-related)
    const baseFilters: SpmApiFilter[] = [
      {
        field: 'deleted',
        operator: SpmFilterOperator.EQUALS,
        values: 0,
      },
    ];

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...baseFilters, ...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/customers',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'customer_id',
        header: '',
        fields: [{
          name: 'customer_id',
          label: t('myShopData.customers.headers.id_customer_shop'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'fullname',
        header: t('myShopData.customers.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('myShopData.customers.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('myShopData.customers.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.customers.headers.creationDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.customers.headers.modifDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'email',
        header: t('myShopData.customers.headers.email'),
        fields: [{
          name: 'email',
          label: t('myShopData.customers.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'gender',
        header: t('myShopData.customers.headers.gender'),
        fields: [{
          name: 'gender',
          label: t('myShopData.customers.headers.gender'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('gender.unknown'), value: '0' },
              { label: t('gender.male'), value: '1' },
              { label: t('gender.female'), value: '2' },
            ],
          },
        }],
      },
      {
        id: 'birthday',
        header: t('myShopData.customers.headers.birthday'),
        fields: [{
          name: 'birth_date',
          label: t('myShopData.customers.headers.birthday'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'newsletter',
        header: t('myShopData.customers.headers.newsletter'),
        fields: [{
          name: 'is_newsletter_subscribed',
          label: t('myShopData.customers.headers.newsletter'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('myLists.manage.customers.optin.0'), value: '0' },
              { label: t('myLists.manage.customers.optin.1'), value: '1' },
              { label: t('myLists.manage.customers.optin.2'), value: '2' },
            ],
          },
        }],
      },
      // Composite: location with country and lang
      {
        id: 'location',
        header: t('myShopData.customers.headers.location'),
        fields: [{
          name: 'country',
          label: t('myShopData.customers.headers.country'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'countrySelect',
          },
        }, {
          name: 'lang',
          label: t('myShopData.customers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.customers.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.customers.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('active'), value: '1' },
              { label: t('inactive'), value: '0' },
            ],
          },
        }],
      },
    ];

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/contacts',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myLists.manage.customers.export.success'));
        // } else {
        //   await showToastError(t('myLists.manage.customers.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      }
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      formatFullName,
      cleanStr,
    };
  },
});
</script>

<style scoped>
/* Fullname composite column */
.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fullname-cell .name-row {
  display: flex;
  align-items: center;
}

.fullname-cell .contact-name {
  font-weight: 600;
  color: #1f2937;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.fullname-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

.fullname-cell .date-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullname-cell .date-info i {
  font-size: 0.625rem;
  color: #9ca3af;
}

/* Location composite column */
.location-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Gender icons */
.gender-icon {
  font-size: 1.125rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.gender-icon:hover {
  transform: scale(1.1);
}

.gender-icon--male {
  color: #3b82f6;
}

.gender-icon--female {
  color: #ec4899;
}

.gender-icon--unknown {
  color: #9ca3af;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

.cursor-pointer {
  cursor: pointer;
}
</style>

<style scoped lang="scss">
/* Page layout */
.list-customers-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
